import { Component, effect, NO_ERRORS_SCHEMA, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Enums } from '../../shared/enums';
import { Utils } from '../../shared/utils';
import { AuthService } from '../../services/auth.service';
import { IMPORTS } from './_imports';
import { Unidade } from 'src/app/models/organograma.model';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FlowDefinitionService } from 'src/app/services/flow-definition.service';
import { FlowInboxStats } from 'src/app/models/home-stats.model';
import { AcessoCidadaoService } from 'src/app/services/acesso-cidadao.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { AnnouncerService } from 'src/app/services/announcer.service';
import { Announcer } from 'src/app/models/announcer.model';

@Component({
    selector: 'home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: true,
    imports: IMPORTS,
    schemas: [NO_ERRORS_SCHEMA]
})
export class HomeComponent implements OnInit {
    // #region [ViewChild]
    @ViewChild('updateHistoryModalRef') updateHistoryModalRef: TemplateRef<HTMLElement>;
    // #endregion

    // #region [Type props]
    Enums = Enums;
    // #endregion

    // #region [props]
    shouldWaitForAcessoCidadaoRenewal = false;
    adminUnitsList: Unidade[] = [];
    isLatestUpdateFound = true;
    updateHistoryDialogRef: MatDialogRef<HTMLElement>;
    // #endregion

    // #region [signals]
    $totalFlowDefinitions = signal(null as number);
    $stats = signal([] as FlowInboxStats[]);
    $announcements = signal([] as Announcer[]);
    // #endregion

    // #region [effects]
    $hideSpinnerEffect = effect(() => {
        if (this.$stats().length > 0) {
            this.spinner.hide('counts');
        }
    });
    // #endregion

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private toastr: ToastrService,
        private flowDefinitionService: FlowDefinitionService,
        private acessoCidadaoService: AcessoCidadaoService,
        private dialog: MatDialog,
        private spinner: NgxSpinnerService,
        private announcerService: AnnouncerService,
        public authService: AuthService,
    ) {
        let querystring = this.route.snapshot.queryParamMap.get('s');
        this.shouldWaitForAcessoCidadaoRenewal = Utils.isNotNullOrEmpty(querystring)
            && atob(decodeURIComponent(querystring)).match(/\d{4}-\d{2}-\d{2}t\d{2}:\d{2}:\d{2}\.\d{3}z/gi) != null;
    }

    /* ====================== */
    // lifecycle methods
    /* ====================== */

    async ngOnInit() {
        this.spinner.show('counts');
        this.getFlowDefinitions();
        this.getAnnouncements();
    }

    goToFlowDefinitionList(guid?: string) {
        if (guid != null) {
            window.localStorage.setItem(Enums.LocalStorageKeys.AdminFlowInbox, btoa(guid || ''));
        }

        this.router.navigate([Enums.PagesPaths.FlowDefinitionList]);
    }

    getUserName(): string {
        return this.authService.$user() != null
            ? (this.authService.$user().apelido || this.authService.$user().nome).trim().split(' ')[0]
            : null;
    }

    getFlowInboxLabel(unit: FlowInboxStats): string {
        const unidade = this.adminUnitsList.find(x => x.guid == unit.unitId);
        let label = unidade.organizacao.organizacaoPai?.sigla != null ? unidade.organizacao.organizacaoPai.sigla + ' - ' : '';
        label += `${unidade.organizacao.sigla} - ${unidade.nomeCurto}`;
        return label;
    }

    onLatestUpdateFound(latestId: string) {
        const savedId = localStorage.getItem(Enums.LocalStorageKeys.UpdateHistoryLastSeenId);

        if (savedId !== latestId) {
            setTimeout(() => {
                this.isLatestUpdateFound = false;
                this.openUpdateHistoryModal(latestId);
            });
        }
    }

    openUpdateHistoryModal(latestId: string) {
        this.updateHistoryDialogRef = this.dialog.open(this.updateHistoryModalRef, {
            minWidth: '800px',
            minHeight: '400px',
            maxHeight: '90vh',
            height: 'auto',
            panelClass: 'update-history-modal',
            data: { latestId },
            autoFocus: true
        });
    }

    dontShowAgain(latestId: string) {
        localStorage.setItem(Enums.LocalStorageKeys.UpdateHistoryLastSeenId, latestId);
        this.updateHistoryDialogRef.close();
    }

    // #region [===== private methods =====]
    // #endregion

    private async getFlowDefinitions() {
        const flowDefinitionResponse = await this.flowDefinitionService.getHomeStats(this.authService.$user()?.id);
        if (!flowDefinitionResponse.isSuccess) {
            this.toastr.error(flowDefinitionResponse.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }
        var stats = flowDefinitionResponse.data;

        const acessoCidadaoResponse = await this.acessoCidadaoService.getUnidadesPerfilAdministrador(this.authService.$user().id);
        if (!acessoCidadaoResponse.isSuccess) {
            this.toastr.error(acessoCidadaoResponse.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }
        this.adminUnitsList = acessoCidadaoResponse.data;

        this.$totalFlowDefinitions.set(stats.flowDefinitionsTotalCount);
        this.$stats.set(stats.flowInboxStats);
    }

    private async getAnnouncements() {
        const announcements = await this.announcerService.get();

        this.$announcements.set(announcements);
    }
}
